import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useUpdateBillableItem, useCreateBillableItem } from '@/hooks';
import { useCreateDeliverable } from '@/hooks/useDeliverables';
import {
  billableItemCreateSchema,
  type BillableItemCreate,
  type BillableItem,
} from '@/lib/schemas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { MonthPicker } from '@/components/ui/month-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { getSuggestedTaxRate } from '@/lib/taxRates';
import { CategorySelect } from '@/components/billable-items/CategorySelect';
import type { BillableItemCategory } from '@/components/billable-items/CategorySelect';
import { CancelBillableItemDialog } from '@/components/billable-items/CancelBillableItemDialog';
import { Trash2, Plus, AlertTriangle, Ban, RotateCcw } from 'lucide-react';

interface BillableItemInlineFormProps {
  mode?: 'create' | 'edit';
  item?: BillableItem;
  projectId: string;
  projectNumber: string | null; // Phase 9.6a: Changed from number to string, null for proposals/templates
  workflowStage?: string; // Batch 7.3.4: Used to set default item_status
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
}

export function BillableItemInlineForm({
  mode = 'edit',
  item,
  projectId,
  projectNumber,
  workflowStage,
  onSave,
  onCancel,
  onDelete,
}: BillableItemInlineFormProps) {
  const updateItem = useUpdateBillableItem();
  const createItem = useCreateBillableItem();
  const createDeliverable = useCreateDeliverable();

  // Cancelled-item spec (R2): cancel/uncancel control + confirmation dialog.
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  // Parse existing deliverables text into titles for edit mode
  const existingDeliverableTitles = (mode === 'edit' && item?.deliverables)
    ? item.deliverables.split('\n').filter((t: string) => t.trim())
    : [];

  const [deliverableTitles, setDeliverableTitles] = useState<string[]>(
    existingDeliverableTitles.length > 0 ? existingDeliverableTitles : ['']
  );
  const deliverableRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleDeliverableChange = useCallback((index: number, value: string) => {
    setDeliverableTitles(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }, []);

  const handleDeliverableKeyDown = useCallback((index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // If current field has text, add new row and focus it
      if (deliverableTitles[index]?.trim()) {
        setDeliverableTitles(prev => {
          const next = [...prev];
          next.splice(index + 1, 0, '');
          return next;
        });
        // Focus next input after render
        setTimeout(() => {
          deliverableRefs.current[index + 1]?.focus();
        }, 0);
      }
    } else if (e.key === 'Backspace' && !deliverableTitles[index] && deliverableTitles.length > 1) {
      e.preventDefault();
      setDeliverableTitles(prev => prev.filter((_, i) => i !== index));
      // Focus previous input
      setTimeout(() => {
        deliverableRefs.current[Math.max(0, index - 1)]?.focus();
      }, 0);
    }
  }, [deliverableTitles]);

  // Batch 7.3.4: Determine default item_status based on workflow_stage
  // - Proposals start with 'proposed' status
  // - Projects start with 'confirmed' status
  // Phase 7.5.13: Check for all proposal stages
  const isProposalStage = ['proposal_draft', 'proposal_sent', 'proposal_declined'].includes(workflowStage || '');
  const defaultItemStatus = mode === 'create'
    ? (isProposalStage ? 'proposed' : 'confirmed')
    : (item?.item_status || 'confirmed');

  // Phase 7.5.3 fix: mirror CategorySelect's own getCategoryDisabledReason rule
  // for the create-mode default so this form never pre-selects a category
  // CategorySelect is about to grey out. Professional Fee stays the default
  // during proposal stages (or when no workflowStage is known — CategorySelect
  // applies no restriction then); everywhere else the default is Add Service,
  // which is a revenue category exactly like Professional Fee (5% GST via
  // src/lib/taxRates.ts), so this never silently drops the new line's tax.
  const defaultCreateCategory = workflowStage && !isProposalStage ? 'add_service' : 'professional_fee';

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<BillableItemCreate>({
    resolver: zodResolver(billableItemCreateSchema),
    defaultValues: mode === 'create' ? {
      project_id: projectId,
      project_number: projectNumber,
      description: '',
      deliverables: '', // Phase 8.3: Deliverables for proposal PDFs
      amount: 0,
      category: defaultCreateCategory, // Phase 7.5.3 fix: workflow-aware default (was hardcoded 'professional_fee')
      cancelled: false,
      status: 'not_started',
      status_notes: '',
      item_status: defaultItemStatus,
      due_date: undefined,
      scheduled_month: undefined,
      // 10c.2: Recurring partnership fee fields
      amount_recurring: null,
      recurring_interval: null,
    } : {
      project_id: item!.project_id,
      // Use item's project_number directly (can be null for proposals/templates)
      project_number: item!.project_number ?? null,
      description: item!.description,
      deliverables: item!.deliverables || '', // Phase 8.3: Deliverables for proposal PDFs
      amount: item!.amount,
      category: item!.category || 'professional_fee',
      cancelled: item!.cancelled,
      status: item!.status || 'not_started',
      status_notes: item!.status_notes || '',
      item_status: item!.item_status || 'confirmed',
      due_date: item!.due_date || undefined,
      // Convert YYYY-MM-DD to YYYY-MM for month input
      scheduled_month: item!.scheduled_month ? item!.scheduled_month.slice(0, 7) : undefined,
      // 10c.2: Recurring partnership fee fields
      amount_recurring: item!.amount_recurring ?? null,
      recurring_interval: item!.recurring_interval ?? null,
    },
  });

  // The Est. Tax preview must use the SAME rule as every creation path
  // (@/lib/taxRates), not a local copy. The local copy hardcoded 5% and omitted
  // 'renewal', so it under-reported tax on a renewal and would show the wrong
  // number outright for any tenant not configured at 5%.
  const { data: invoiceDefaults } = useQuery({
    queryKey: ['settings', 'invoice-defaults'],
    queryFn: async () => {
      try {
        return await api.settings.getInvoiceDefaults();
      } catch {
        return null; // fall back to the module default rate
      }
    },
    staleTime: 5 * 60 * 1000,
  });
  const tenantTaxRate = invoiceDefaults?.tax_rate ?? null;

  const category = watch('category');
  // 10c.2: Watch recurring amount to conditionally show interval dropdown
  const amountRecurring = watch('amount_recurring');

  // Phase 7 Batch 2: Auto-set amount to $0 when Internal Work selected
  useEffect(() => {
    if (category === 'internal_work') {
      setValue('amount', 0);
    }
  }, [category, setValue]);

  const onSubmit = async (data: BillableItemCreate) => {
    console.log('🟢 Form onSubmit called with data:', data);
    // Sync deliverable titles into the text field (for proposal PDF backward compat)
    const nonEmptyTitles = deliverableTitles.filter(t => t.trim());
    data.deliverables = nonEmptyTitles.length > 0 ? nonEmptyTitles.join('\n') : null;

    try {
      if (mode === 'create') {
        const response = await createItem.mutateAsync(data);
        // After billable item is created, create linked deliverable entities
        const createdItemId = response?.billable_item?.id;
        if (createdItemId && nonEmptyTitles.length > 0) {
          // Create deliverables in sequence (order matters for sort_order)
          for (const title of nonEmptyTitles) {
            try {
              await createDeliverable.mutateAsync({
                projectId: data.project_id,
                deliverable: {
                  title: title.trim(),
                  billable_item_id: createdItemId,
                },
              });
            } catch (err) {
              console.error('Failed to create deliverable:', title, err);
            }
          }
        }
      } else {
        // Strip project_id and project_number from update payload -
        // these are create-only fields. Sending project_id triggers
        // a super-admin-only reassignment check on the backend.
        const { project_id, project_number, ...updateData } = data;
        await updateItem.mutateAsync({ id: item!.id, data: updateData });

        // Create deliverable entities for any NEW titles added during edit
        const originalSet = new Set(existingDeliverableTitles.map((t: string) => t.trim().toLowerCase()));
        const newTitles = nonEmptyTitles.filter(t => !originalSet.has(t.trim().toLowerCase()));
        if (newTitles.length > 0 && item?.id) {
          for (const title of newTitles) {
            try {
              await createDeliverable.mutateAsync({
                projectId: item.project_id,
                deliverable: {
                  title: title.trim(),
                  billable_item_id: item.id,
                },
              });
            } catch (err) {
              console.error('Failed to create deliverable:', title, err);
            }
          }
        }
      }
      onSave();
    } catch (error) {
      // Mutation hooks handle error toasts via onError - just log for debugging
      console.error('Form submission error:', error);
    }
  };

  // Debug: Log form state when it changes
  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      console.log('🔵 Form field changed:', { name, type, value: value[name as keyof typeof value], errors: errors });
    });
    return () => subscription.unsubscribe();
  }, [watch, errors]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const formValues = watch();
        console.log('🔴 Form onSubmit event triggered', {
          errors,
          isValid: Object.keys(errors).length === 0,
          formValues
        });

        // Call handleSubmit and catch any validation errors
        handleSubmit(
          (data) => {
            console.log('🟢 Form validation PASSED, calling onSubmit with:', data);
            onSubmit(data);
          },
          (validationErrors) => {
            console.error('❌ Form validation FAILED with errors:', validationErrors);
            console.error('Form values that failed validation:', formValues);
          }
        )(e);
      }}
      className="space-y-4 py-4"
      onKeyDown={(e) => {
        // Ctrl+Enter or Cmd+Enter = submit from anywhere (including from textarea)
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          handleSubmit(onSubmit)();
        }
      }}
    >
      {/* Warning: invoiced item protection */}
      {mode === 'edit' && item && (item.invoiced_amount || 0) > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">
              This item has been invoiced ({new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(item.invoiced_amount || 0)})
            </p>
            <p className="text-sm text-amber-700 mt-0.5">
              Changes here will <b>not</b> update the invoice. Editing the description or amount will create a mismatch with the invoice.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Description */}
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="description">
            Description <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="description"
            {...register('description')}
            placeholder="Describe the billable work (supports multiple lines, use Ctrl+Enter to save)"
            rows={3}
            className={`resize-y ${errors.description ? 'border-destructive' : ''}`}
          />
          {errors.description && (
            <p className="text-sm text-destructive">{errors.description.message}</p>
          )}
        </div>

        {/* Deliverables — dynamic input list, creates tracked deliverable entities */}
        <div className="space-y-2 md:col-span-2">
          <Label>Deliverables</Label>
          <div className="space-y-1.5">
            {deliverableTitles.map((title, index) => (
              <div key={index} className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground w-5 text-right shrink-0">{index + 1}.</span>
                <Input
                  ref={(el) => { deliverableRefs.current[index] = el; }}
                  value={title}
                  onChange={(e) => handleDeliverableChange(index, e.target.value)}
                  onKeyDown={(e) => handleDeliverableKeyDown(index, e)}
                  placeholder={index === 0 ? "e.g. Existing Drawing Package" : "Add another deliverable..."}
                  className="h-8 text-sm flex-1"
                />
                {deliverableTitles.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0 opacity-0 hover:opacity-100 focus:opacity-100"
                    tabIndex={-1}
                    onClick={() => setDeliverableTitles(prev => prev.filter((_, i) => i !== index))}
                  >
                    <Trash2 className="h-3 w-3 text-muted-foreground" />
                  </Button>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground h-7 px-2"
              onClick={() => {
                setDeliverableTitles(prev => [...prev, '']);
                setTimeout(() => {
                  deliverableRefs.current[deliverableTitles.length]?.focus();
                }, 0);
              }}
            >
              <Plus className="h-3 w-3 mr-1" />
              Add deliverable
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {mode === 'create'
              ? 'Each item creates a tracked deliverable linked to this billable item. Press Enter to add more.'
              : 'Add new items to create tracked deliverables. Existing deliverables are managed in the Deliverables section below.'}
          </p>
        </div>

        {/* Batch 7.3.4: Category (50%), Amount (30%), Estimated Tax (20%) */}
        {category !== 'internal_work' && (
          <>
            {/* Category - 50% width */}
            <div className="space-y-2 md:col-span-1">
              <Label htmlFor="category">
                Category <span className="text-destructive">*</span>
              </Label>
              <CategorySelect
                value={category as BillableItemCategory}
                onChange={(value) => setValue('category', value)}
                workflowStage={workflowStage}
              />
              {errors.category && (
                <p className="text-sm text-destructive">{errors.category.message}</p>
              )}
            </div>

            {/* Amount and Estimated Tax - combined 50% width */}
            <div className="grid grid-cols-5 gap-4 md:col-span-1">
              {/* Amount - 60% of the 50% = 30% total */}
              <div className="space-y-2 col-span-3">
                <Label htmlFor="amount">
                  Amount <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    $
                  </span>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    {...register('amount', { valueAsNumber: true })}
                    className={`pl-7 ${errors.amount ? 'border-destructive' : ''}`}
                    placeholder="0.00"
                    onFocus={(e) => e.target.select()}
                  />
                </div>
                {errors.amount && (
                  <p className="text-sm text-destructive">{errors.amount.message}</p>
                )}
              </div>

              {/* Estimated Tax - 40% of the 50% = 20% total */}
              <div className="space-y-2 col-span-2">
                <Label htmlFor="estimated_tax" className="text-xs">
                  Est. Tax
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    $
                  </span>
                  <Input
                    id="estimated_tax"
                    type="text"
                    value={(() => {
                      const amount = watch('amount') || 0;
                      const taxRate = getSuggestedTaxRate(category, tenantTaxRate);
                      const taxAmount = Math.round(amount * (taxRate / 100) * 100) / 100;
                      return new Intl.NumberFormat('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(taxAmount);
                    })()}
                    readOnly
                    className="pl-7 bg-muted/50 cursor-not-allowed text-sm"
                    title="Estimated tax (5% for Professional Fee/Add Service, 0% for others)"
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Category for Internal Work */}
        {category === 'internal_work' && (
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="category">
              Category <span className="text-destructive">*</span>
            </Label>
            <CategorySelect
              value={category as BillableItemCategory}
              onChange={(value) => setValue('category', value)}
              workflowStage={workflowStage}
            />
            {errors.category && (
              <p className="text-sm text-destructive">{errors.category.message}</p>
            )}
          </div>
        )}

        {/* 10c.2: Recurring/Partnership Fee (proposal-only, never invoiced) */}
        {category !== 'internal_work' && (
          <div className="md:col-span-2 space-y-2 rounded-lg border border-muted p-3">
            <p className="text-sm font-medium">Recurring / Partnership Fee <span className="text-muted-foreground font-normal text-xs">(optional — proposals only, not invoiced)</span></p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="amount_recurring" className="text-xs">Recurring Amount</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="amount_recurring"
                    type="number"
                    step="0.01"
                    {...register('amount_recurring', { valueAsNumber: true, setValueAs: (v) => (v === '' || isNaN(v) ? null : Number(v)) })}
                    className={`pl-7 h-8 text-sm ${errors.amount_recurring ? 'border-destructive' : ''}`}
                    placeholder="0.00"
                    onFocus={(e) => e.target.select()}
                  />
                </div>
                {errors.amount_recurring && (
                  <p className="text-xs text-destructive">{errors.amount_recurring.message}</p>
                )}
              </div>
              {/* Interval — only visible when amount_recurring > 0 */}
              {amountRecurring != null && amountRecurring > 0 && (
                <div className="space-y-1">
                  <Label htmlFor="recurring_interval" className="text-xs">Billing Interval <span className="text-destructive">*</span></Label>
                  <Select
                    value={watch('recurring_interval') || ''}
                    onValueChange={(value) => setValue('recurring_interval', value as 'monthly' | 'quarterly' | 'annual', { shouldValidate: true })}
                  >
                    <SelectTrigger type="button" id="recurring_interval" className={`h-8 text-sm ${errors.recurring_interval ? 'border-destructive' : ''}`}>
                      <SelectValue placeholder="Select interval" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.recurring_interval && (
                    <p className="text-xs text-destructive">{errors.recurring_interval.message}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Invoice Tracking - Batch 7.3.4: Show below Category/Amount/Tax */}
        {mode === 'edit' && item && category !== 'internal_work' && (
          <>
            <div className="space-y-2">
              <Label htmlFor="invoiced_amount">Invoiced</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  $
                </span>
                <Input
                  id="invoiced_amount"
                  type="text"
                  value={new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(item.invoiced_amount || 0)}
                  readOnly
                  className="pl-7 bg-muted/50 cursor-not-allowed"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="remaining_amount">Remaining</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  $
                </span>
                <Input
                  id="remaining_amount"
                  type="text"
                  value={new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(item.remaining_amount || 0)}
                  readOnly
                  className="pl-7 bg-muted/50 cursor-not-allowed"
                />
              </div>
            </div>
          </>
        )}

        {/* Expected Invoice Month (Cash Flow Planning) - Phase 7.5.12 */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="scheduled_month">Expected Invoice Month</Label>
          <MonthPicker
            id="scheduled_month"
            value={watch('scheduled_month') || null}
            onChange={(value) => setValue('scheduled_month', value, { shouldValidate: true })}
            placeholder="Select month"
            className={errors.scheduled_month ? 'border-destructive' : ''}
          />
          {errors.scheduled_month && (
            <p className="text-sm text-destructive">{errors.scheduled_month.message}</p>
          )}
          {/* Overdue warning */}
          {mode === 'edit' && item?.scheduled_month && !item?.invoiced_amount && (() => {
            const scheduledDate = new Date(item.scheduled_month);
            const currentMonth = new Date(new Date().toISOString().slice(0, 7) + '-01');
            return scheduledDate < currentMonth;
          })() && (
            <p className="text-sm text-amber-600">
              Overdue: was scheduled for {new Date(item.scheduled_month).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
            </p>
          )}
        </div>

        {/* Status */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="status">Status</Label>
          <Select
            value={watch('status')}
            onValueChange={(value) => setValue('status', value as 'not_started' | 'in_progress' | 'complete' | 'on_hold')}
          >
            <SelectTrigger type="button">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="not_started">Not Started</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="complete">Complete</SelectItem>
              <SelectItem value="on_hold">On Hold</SelectItem>
            </SelectContent>
          </Select>
          {errors.status && (
            <p className="text-sm text-destructive">{errors.status.message}</p>
          )}
        </div>

        {/* Status Notes */}
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="status_notes">Status Notes</Label>
          <Textarea
            id="status_notes"
            {...register('status_notes')}
            placeholder="Add notes about current status (optional)"
            className={errors.status_notes ? 'border-destructive' : ''}
          />
          {errors.status_notes && (
            <p className="text-sm text-destructive">{errors.status_notes.message}</p>
          )}
        </div>

        {/* Item Status (Lifecycle) - Phase 7 Batch 7.1 */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="item_status">Item Status</Label>
          <Select
            value={watch('item_status') || 'confirmed'}
            onValueChange={(value) => setValue('item_status', value as 'proposed' | 'proposed-optional' | 'confirmed' | 'declined')}
          >
            <SelectTrigger type="button" id="item_status">
              <SelectValue placeholder="Select item status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="proposed">Proposed (Base Scope)</SelectItem>
              <SelectItem value="proposed-optional">Proposed (Optional)</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="declined">Declined</SelectItem>
            </SelectContent>
          </Select>
          {errors.item_status && (
            <p className="text-sm text-destructive">{errors.item_status.message}</p>
          )}
        </div>

        {/* Due Date (Migration V011) */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="due_date">Due Date</Label>
          <Input
            id="due_date"
            type="date"
            {...register('due_date')}
            className={errors.due_date ? 'border-destructive' : ''}
          />
          {errors.due_date && (
            <p className="text-sm text-destructive">{errors.due_date.message}</p>
          )}
        </div>

        {/* Internal Work Info (Phase 7 Batch 2) */}
        {category === 'internal_work' && (
          <div className="md:col-span-2 rounded-lg border border-muted bg-muted/50 p-3">
            <p className="text-sm font-medium mb-1">Internal Work Tracking</p>
            <p className="text-sm text-muted-foreground">
              Internal work items are always $0 (not billable). Use status and notes to track progress.
            </p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-between pt-2">
        {mode === 'edit' && (
          <div className="flex items-center gap-2">
            {onDelete && (
              <Button
                type="button"
                variant="destructive"
                onClick={onDelete}
                disabled={item!.invoiced_amount > 0}
                title={item!.invoiced_amount > 0 ? "Cannot delete item with invoices" : "Delete item"}
                size="sm"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            )}
            {/* Cancelled-item spec (R2): explicit cancel / uncancel control. */}
            {item!.cancelled ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setCancelDialogOpen(true)}
                title="Restore this item so it can be invoiced again"
                size="sm"
                data-testid="uncancel-item-button"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Restore
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => setCancelDialogOpen(true)}
                title="Cancel this item (removes it from any draft/ready invoice)"
                size="sm"
                data-testid="cancel-item-button"
              >
                <Ban className="mr-2 h-4 w-4" />
                Cancel Item
              </Button>
            )}
          </div>
        )}
        {mode === 'create' && <div />}

        <div className="space-x-2">
          <Button type="button" variant="outline" onClick={onCancel} size="sm" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={isSubmitting}>
            {isSubmitting ? (mode === 'create' ? 'Creating...' : 'Saving...') : (mode === 'create' ? 'Create' : 'Save')}
          </Button>
        </div>
      </div>

      {/* Cancelled-item spec (R2): pre-check + block/confirm/uncancel dialog. */}
      {mode === 'edit' && item && (
        <CancelBillableItemDialog
          open={cancelDialogOpen}
          onOpenChange={setCancelDialogOpen}
          item={item}
        />
      )}
    </form>
  );
}
